import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { DrunkCard } from '../components/DrunkCard';
import { PinInput } from '../components/PinInput';
import { useSession } from '../context/SessionContext';
import { ArrowLeft, LogIn } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const JoinSession = () => {
  const navigate = useNavigate();
  const { saveSession } = useSession();
  
  const [step, setStep] = useState(1);
  const [sessionCode, setSessionCode] = useState('');
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleJoinSession = async () => {
    if (!name.trim()) {
      toast.error("What's your name, party crasher?");
      return;
    }
    if (!isValidEmail(email)) {
      toast.error("Need a valid email!");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/sessions/join`, {
        session_code: sessionCode.toUpperCase(),
        pin: pin,
        member_name: name.trim(),
        member_email: email.trim().toLowerCase()
      });
      
      saveSession({
        ...response.data,
        pin: pin,
        member_name: name.trim(),
        member_email: email.trim().toLowerCase()
      });
      
      toast.success("You're in! 🎉");
      navigate(`/session/${response.data.session_code}`);
    } catch (error) {
      const message = error.response?.data?.detail || "Couldn't join the party!";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream p-4">
      <div className="max-w-md mx-auto">
        {/* Back Button */}
        <DrunkButton
          variant="ghost"
          onClick={() => step > 1 ? setStep(step - 1) : navigate('/')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 inline mr-1" />
          {step > 1 ? 'Back' : 'Home'}
        </DrunkButton>

        {/* Step 1: Enter Session Code */}
        {step === 1 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Join the Party! 🎊
              </h2>
              
              <p className="font-body text-sm mb-4 text-black/70">
                Enter the session code:
              </p>
              
              <input
                type="text"
                placeholder="e.g., PARTY-1234"
                value={sessionCode}
                onChange={(e) => setSessionCode(e.target.value.toUpperCase())}
                className="w-full p-4 text-xl text-center border-2 border-black rounded-xl mb-4 font-mono font-bold tracking-wider shadow-brutal focus:outline-none focus:border-pink uppercase"
                maxLength={15}
                data-testid="session-code-input"
                autoComplete="off"
              />
              
              <DrunkButton
                variant="cyan"
                size="large"
                onClick={() => setStep(2)}
                disabled={!sessionCode.trim()}
                className="w-full py-4"
                data-testid="next-pin-btn"
              >
                Next: Enter PIN 🔐
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 2: Enter PIN */}
        {step === 2 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Enter the PIN 🤫
              </h2>
              <p className="font-body text-sm mb-6 text-black/70">
                Session: <span className="font-mono font-bold">{sessionCode}</span>
              </p>
              
              <div className="mb-6">
                <PinInput
                  value={pin}
                  onChange={setPin}
                  length={4}
                />
              </div>
              
              <DrunkButton
                variant="accent"
                size="large"
                onClick={() => setStep(3)}
                disabled={pin.length !== 4}
                className="w-full py-4"
                data-testid="next-name-btn"
              >
                Next: Your Name 👤
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 3: Enter Name */}
        {step === 3 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
                Who's Joining? 🍻
              </h2>
              
              <input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                maxLength={50}
                data-testid="join-name-input"
                autoComplete="off"
              />
              
              <DrunkButton
                variant="cyan"
                size="large"
                onClick={() => setStep(4)}
                disabled={!name.trim()}
                className="w-full py-4"
                data-testid="next-name-btn"
              >
                Next: Your Email 📧
              </DrunkButton>
            </DrunkCard>
          </div>
        )}

        {/* Step 4: Enter Email */}
        {step === 4 && (
          <div className="bounce-in">
            <DrunkCard variant="default" className="text-center p-5">
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                Your Email 📧
              </h2>
              <p className="font-body text-sm mb-4 text-black/70">
                So you can rejoin if you drop out
              </p>
              
              <input
                type="email"
                placeholder="drunk@party.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full p-4 text-lg border-2 border-black rounded-xl mb-4 font-body shadow-brutal focus:outline-none focus:border-pink"
                maxLength={100}
                data-testid="join-email-input"
                autoComplete="email"
              />
              
              <DrunkButton
                variant="primary"
                size="large"
                onClick={handleJoinSession}
                disabled={!email.trim() || !isValidEmail(email) || isLoading}
                className="w-full py-4"
                data-testid="join-session-btn"
              >
                <LogIn className="w-5 h-5 inline mr-2" />
                {isLoading ? 'Joining...' : 'Join!'}
              </DrunkButton>
            </DrunkCard>
          </div>
        )}
      </div>
    </div>
  );
};

export default JoinSession;
